import { useEffect, useContext } from 'react';
import { useMutation } from 'react-query';

import { navigateWithParams } from '@creditasmx-utilities/helpers';

import { createLead } from '../../requests/externalAutoForm.api';
import { getQueryParams } from './ExternalAutoForm.utils';
import {
  CreateLeadPayload,
  CreateLeadResponse,
} from './ExternalAutoForm.types';
import { UTM_NOT_ALLOWED } from './ExternalAutoForm.constants';
import { LocalStorageKeys } from '../../constants';
import { useTracking } from '../../../shared/utilities/tagManager/tagManager.hook';
import { TrackingCategory } from '../../tracking/tracking.constants';
import { ExternalAutoFormContext } from '../../pages/mfExternalAutoForm.context';

const queryParams = getQueryParams();
const { item_id: externalAutoId } = queryParams;

export const useExternalAutoForm = () => {
  const { trackPageLoad } = useTracking();
  const {
    goTo,
    leadIdState: [leadId, setLeadIdContext],
  } = useContext(ExternalAutoFormContext);

  const { mutateAsync: mutateCreateLead } = useMutation(
    async ({
      data,
      companyId,
    }: {
      companyId: string;
      data: CreateLeadPayload;
    }): Promise<CreateLeadResponse> =>
      (await createLead(companyId).request({ data })).data,
  );

  const setLeadId = (leadId: string): void => {
    localStorage.setItem(LocalStorageKeys.LEAD_ID, leadId);
    setLeadIdContext(leadId);
  };

  useEffect(() => {
    trackPageLoad({
      page: {
        action: '',
        category: TrackingCategory.EXTERNAL_AUTO_PAGE,
      },
      event: {
        label: '',
        leadId,
      },
    });
  }, []);

  useEffect(() => {
    const { companyId, sessionId } = queryParams;
    const createLead = async (): Promise<void> => {
      const utm = queryParams;
      UTM_NOT_ALLOWED.forEach((notAllowedParam) => delete utm[notAllowedParam]);
      localStorage.setItem(LocalStorageKeys.UTM, JSON.stringify(utm));
      const { id } = await mutateCreateLead({
        companyId,
        data: {
          utm,
          externalAutoId,
        },
      });
      setLeadId(id);
    };
    if (sessionId) {
      setLeadId(sessionId);
    } else if (
      leadId &&
      localStorage.getItem(LocalStorageKeys.EXTERNAL_AUTO_ID)
    ) {
      localStorage.setItem(LocalStorageKeys.EXTERNAL_AUTO_ID, externalAutoId);
      navigateWithParams(goTo.toHasAnApplication, `leadId=${leadId}`);
    } else if (externalAutoId && companyId) {
      createLead();
    }
  }, []);

  return { externalAutoId };
};
